import { resolveRequestUrl } from '../api/config';
import { apiAxios } from '../api/axiosClient';
import { resolveGcpImageForCanvas, fetchGcpImageBlobUrl, isGcpImageStreamUrl } from '../api/gcpImagesApi';
import { getLocalFontBinary, putLocalFontBinary } from './fontLocalCache';

const SYSTEM_FONTS = new Set([
  'arial', 'helvetica', 'times new roman', 'times', 'courier new', 'courier',
  'verdana', 'georgia', 'palatino', 'garamond', 'bookman', 'comic sans ms',
  'trebuchet ms', 'impact', 'symbol', 'tahoma',
]);

export function isSystemFont(family) {
  return SYSTEM_FONTS.has(String(family || '').toLowerCase().trim());
}

/** Use flyer font proxy on deployed hosts (CFS CSP blocks fonts.googleapis.com). */
export function shouldUseFontProxy() {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host !== 'localhost' && host !== '127.0.0.1';
}

export function googleFontsCssUrl(family) {
  const name = String(family || '').trim();
  if (!name) return '';
  if (shouldUseFontProxy()) {
    return resolveRequestUrl(`/api/fonts/css?family=${encodeURIComponent(name)}`);
  }
  const enc = name.replace(/ /g, '+');
  return `https://fonts.googleapis.com/css2?family=${enc}:ital,wght@0,400;0,600;0,700;1,400;1,700&display=swap`;
}

/** Konva `fontStyle` string from scene typography (bold / italic / both). */
export function konvaFontStyleFromTypography(ty = {}) {
  const bold =
    ty.fontWeight === 'bold' ||
    ty.fontWeight === '700' ||
    ty.fontWeight === 600 ||
    ty.fontWeight === '600';
  const italic = ty.fontStyle === 'italic';
  if (italic && bold) return 'italic bold';
  if (italic) return 'italic';
  if (bold) return 'bold';
  return 'normal';
}

function typographyWeight(ty = {}) {
  const bold =
    ty.fontWeight === 'bold' ||
    ty.fontWeight === '700' ||
    ty.fontWeight === 600 ||
    ty.fontWeight === '600';
  return bold ? '700' : '400';
}

function typographyItalic(ty = {}) {
  return ty.fontStyle === 'italic';
}

export function collectFontFamiliesFromObjects(objects) {
  const families = new Set();
  for (const o of objects || []) {
    if (o.type !== 'text') continue;
    const family = o.typography?.fontFamily;
    if (family && !isSystemFont(family)) families.add(family);
  }
  return [...families];
}

/** CSS font strings for `document.fonts.load()` based on actual text styling. */
export function collectFontLoadSpecsFromObjects(objects) {
  const specs = new Set();
  for (const o of objects || []) {
    if (o.type !== 'text') continue;
    const ty = o.typography || {};
    const family = ty.fontFamily;
    if (!family || isSystemFont(family)) continue;
    const weight = typographyWeight(ty);
    const italic = typographyItalic(ty);
    specs.add(`${italic ? 'italic ' : ''}${weight} 16px "${family}"`);
  }
  return [...specs];
}

function waitForStylesheet(link) {
  return new Promise((resolve) => {
    const finish = (ok) => resolve(ok);

    try {
      if (link.sheet) {
        finish(true);
        return;
      }
    } catch {
      /* cross-origin stylesheet ??? fall through */
    }

    if (link.dataset.fontLoaded === '1') {
      finish(true);
      return;
    }
    if (link.dataset.fontLoaded === 'error') {
      finish(false);
      return;
    }

    const onLoad = () => {
      link.dataset.fontLoaded = '1';
      finish(true);
    };
    const onError = () => {
      link.dataset.fontLoaded = 'error';
      finish(false);
    };

    link.addEventListener('load', onLoad, { once: true });
    link.addEventListener('error', onError, { once: true });
  });
}

/**
 * Inject Google Fonts stylesheets and wait for them to parse.
 * Skips built-in system fonts.
 */
export async function ensureGoogleFontsLoaded(families) {
  const unique = [...new Set((families || []).filter((f) => f && !isSystemFont(f)))];
  if (!unique.length) return;

  await Promise.race([
    Promise.allSettled(
      unique.map(async (font) => {
        const enc = font.replace(/ /g, '+');
        const href = googleFontsCssUrl(font);
        if (pdfFontLogDepth > 0) {
          pdfFontLog('CSS request', { family: font, url: href });
        }
        const existing = document.querySelector(`link[data-google-font="${enc}"]`);
        if (existing) {
          await waitForStylesheet(existing);
          return;
        }
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.dataset.googleFont = enc;
        document.head.appendChild(link);
        const ok = await waitForStylesheet(link);
        if (pdfFontLogDepth > 0) {
          pdfFontLog(ok ? 'CSS loaded' : 'CSS failed', { family: font, url: href });
          if (!ok) {
            pdfFontLogFail('css_load_failed', { family: font, endpoint: href });
          }
        }

        if (shouldUseFontProxy() && !ok) {
          link.href = `https://fonts.googleapis.com/css2?family=${enc}:ital,wght@0,400;0,600;0,700;1,400;1,700&display=swap`;
          link.dataset.googleFontProxyFallback = '1';
          await waitForStylesheet(link);
        }
      }),
    ),
    new Promise((r) => setTimeout(r, 12000)),
  ]);

  await document.fonts.ready;
}

const GOOGLE_FONTS_TTF_UA =
  'Mozilla/5.0 (compatible; MSIE 10.0; Windows NT 6.1; Trident/6.0)';

const fontFaceCache = new Map();

let pdfFontLogDepth = 0;
const pdfFontFailures = [];

function fontBinaryApiPath(family, weight, italic) {
  const params = new URLSearchParams({
    family: String(family),
    weight: String(weight),
    italic: italic ? '1' : '0',
  });
  return `/api/fonts/binary?${params}`;
}

function fontCssApiPath(family) {
  return `/api/fonts/css?family=${encodeURIComponent(String(family || '').trim())}`;
}

function fontBinaryApiUrl(family, weight, italic) {
  return resolveRequestUrl(fontBinaryApiPath(family, weight, italic));
}

function fontCssApiUrl(family) {
  return resolveRequestUrl(fontCssApiPath(family));
}

function pdfFontLog(message, detail = undefined) {
  if (pdfFontLogDepth <= 0) return;
  if (detail !== undefined) {
    console.log('[pdf-fonts]', message, detail);
  } else {
    console.log('[pdf-fonts]', message);
  }
}

function pdfFontLogFail(reason, detail = {}) {
  if (pdfFontLogDepth <= 0) return;
  const entry = { reason, ...detail };
  pdfFontFailures.push(entry);
  console.warn('[pdf-fonts] FAIL', entry);
}

function runWithPdfFontLogging(fn) {
  pdfFontLogDepth += 1;
  pdfFontFailures.length = 0;
  return Promise.resolve()
    .then(fn)
    .finally(() => {
      if (pdfFontFailures.length) {
        console.warn('[pdf-fonts] export finished with font failures', {
          count: pdfFontFailures.length,
          failures: pdfFontFailures,
        });
      }
      pdfFontLogDepth -= 1;
    });
}

function fontVariantLabel(family, weight, italic) {
  return `${family} ${weight}${italic ? ' italic' : ''}`;
}

function fontFaceKey(family, weight, italic) {
  const isItalic = italic === true || italic === 'italic';
  return `${family}\0${weight}\0${isItalic ? 'italic' : 'normal'}`;
}

function detectFontFormat(buffer) {
  if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 4) {
    return { format: 'woff2', mime: 'font/woff2' };
  }
  const sig = String.fromCharCode(...new Uint8Array(buffer.slice(0, 4)));
  if (sig === 'wOF2') return { format: 'woff2', mime: 'font/woff2' };
  if (sig === 'wOFF') return { format: 'woff', mime: 'font/woff' };
  if (sig === 'OTTO' || sig.startsWith('\0')) return { format: 'opentype', mime: 'font/otf' };
  return { format: 'truetype', mime: 'font/ttf' };
}

function arrayBufferToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    s += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(s);
}

function injectFontFaceStyleTag(family, weight, italic, buffer) {
  const styleId = `flyer-ff-${String(family).replace(/\s+/g, '-')}-${weight}-${italic ? 'i' : 'n'}`;
  if (document.getElementById(styleId)) return;
  const { format, mime } = detectFontFormat(buffer);
  const style = document.createElement('style');
  style.id = styleId;
  const b64 = arrayBufferToBase64(buffer);
  style.textContent =
    `@font-face{font-family:"${String(family).replace(/"/g, '')}";` +
    `font-weight:${weight};font-style:${italic ? 'italic' : 'normal'};` +
    `src:url(data:${mime};base64,${b64}) format('${format}');font-display:swap;}`;
  document.head.appendChild(style);
}

/** Copy embedded @font-face rules into an export container (html2canvas only clones the subtree). */
export function appendEmbeddedFontStylesToElement(el) {
  if (!el || typeof document === 'undefined') return;
  if (el.dataset.flyerFontsInjected === '1') return;
  const styles = [...document.querySelectorAll('style[id^="flyer-ff-"]')];
  if (!styles.length) return;
  const frag = document.createDocumentFragment();
  styles.forEach((style) => {
    const clone = style.cloneNode(true);
    clone.id = `${style.id}-export`;
    frag.appendChild(clone);
  });
  el.insertBefore(frag, el.firstChild);
  el.dataset.flyerFontsInjected = '1';
}

async function fetchFontBinaryDirect(family, weight, italic) {
  const q = `${String(family).replace(/ /g, '+')}:ital,wght@${italic ? 1 : 0},${weight}`;
  const css = await fetch(`https://fonts.googleapis.com/css2?family=${q}&display=swap`, {
    headers: { 'User-Agent': GOOGLE_FONTS_TTF_UA },
  })
    .then((r) => (r.ok ? r.text() : ''))
    .catch(() => '');

  const match = css.match(/url\((https?:\/\/[^)]+\.(?:woff2?|ttf|otf)[^)]*)\)/i);
  if (!match) return null;
  return fetch(match[1])
    .then((r) => (r.ok ? r.arrayBuffer() : null))
    .catch(() => null);
}

async function fetchFontBinaryFromProxy(family, weight, italic) {
  const path = fontBinaryApiPath(family, weight, italic);
  const url = fontBinaryApiUrl(family, weight, italic);
  const label = fontVariantLabel(family, weight, italic);
  const started = Date.now();
  pdfFontLog('binary API request', { variant: label, url });

  try {
    const res = await apiAxios.get(path, {
      responseType: 'arraybuffer',
      skipGlobalLoader: true,
    });
    const bytes = res.data?.byteLength ?? 0;
    if (res.status >= 200 && res.status < 300 && res.data) {
      pdfFontLog('binary API ok', {
        variant: label,
        url,
        status: res.status,
        bytes,
        ms: Date.now() - started,
      });
      return res.data;
    }
    pdfFontLog('binary API empty response', {
      variant: label,
      endpoint: url,
      status: res.status,
      ms: Date.now() - started,
    });
    pdfFontLogFail('binary_empty_response', { variant: label, endpoint: url, status: res.status });
    return null;
  } catch (err) {
    const failDetail = {
      variant: label,
      endpoint: url,
      status: err?.response?.status,
      responseBody: err?.response?.data,
      message: err?.message || String(err),
      ms: Date.now() - started,
    };
    pdfFontLog('binary API error', failDetail);
    pdfFontLogFail('binary_api_error', failDetail);
    throw err;
  }
}

async function fetchFontBinary(family, weight, italic) {
  const label = fontVariantLabel(family, weight, italic);
  if (shouldUseFontProxy()) {
    try {
      const proxied = await fetchFontBinaryFromProxy(family, weight, italic);
      if (proxied) return proxied;
      pdfFontLog('binary proxy miss, trying direct Google Fonts', { variant: label });
    } catch {
      pdfFontLog('binary proxy failed, trying direct Google Fonts', { variant: label });
    }
  } else {
    pdfFontLog('binary direct Google Fonts', { variant: label });
  }

  const direct = await fetchFontBinaryDirect(family, weight, italic);
  if (pdfFontLogDepth > 0) {
    pdfFontLog(direct ? 'binary direct ok' : 'binary direct failed', { variant: label });
    if (!direct) {
      pdfFontLogFail('binary_direct_failed', {
        variant: label,
        endpoint: `https://fonts.googleapis.com/css2?family=${String(family).replace(/ /g, '+')}`,
      });
    }
  }
  return direct;
}

function fontLoadSpec(family, weight, italic) {
  return `${italic ? 'italic ' : ''}${weight} 16px "${family}"`;
}

async function waitForFontCheck(family, weight, italic, { timeoutMs = 8000 } = {}) {
  const spec = fontLoadSpec(family, weight, italic);
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      if (document.fonts.check(spec)) return true;
      await document.fonts.load(spec);
      if (document.fonts.check(spec)) return true;
    } catch {
      /* ignore */
    }
    await new Promise((r) => setTimeout(r, 40));
  }
  return document.fonts.check(spec);
}

async function loadFontBinary(family, weight, italic) {
  const key = fontFaceKey(family, weight, italic);
  const label = fontVariantLabel(family, weight, italic);
  const cached = await getLocalFontBinary(key);
  if (cached) {
    pdfFontLog('binary from IndexedDB cache', { variant: label, bytes: cached.byteLength });
    return cached;
  }
  pdfFontLog('binary cache miss', { variant: label });
  const fetched = await fetchFontBinary(family, weight, italic);
  if (fetched) {
    void putLocalFontBinary(key, fetched);
    pdfFontLog('binary cached to IndexedDB', { variant: label, bytes: fetched.byteLength });
  } else if (pdfFontLogDepth > 0) {
    pdfFontLogFail('binary_unavailable', {
      variant: label,
      endpoint: fontBinaryApiUrl(family, weight, italic),
      cssEndpoint: fontCssApiUrl(family),
    });
  }
  return fetched;
}

async function ensureFontFaceEmbedded(family, weight, italic) {
  const style = italic ? 'italic' : 'normal';
  const key = fontFaceKey(family, weight, italic);
  if (fontFaceCache.has(key)) return fontFaceCache.get(key);

  const buffer = await loadFontBinary(family, weight, italic);
  if (!buffer) {
    fontFaceCache.set(key, false);
    pdfFontLogFail('fontface_no_binary', {
      variant: fontVariantLabel(family, weight, italic),
      binaryEndpoint: fontBinaryApiUrl(family, weight, italic),
      cssEndpoint: fontCssApiUrl(family),
    });
    return false;
  }

  try {
    injectFontFaceStyleTag(family, weight, italic, buffer);
    const face = new FontFace(family, buffer, { weight: String(weight), style });
    await face.load();
    document.fonts.add(face);
    fontFaceCache.set(key, true);
    await waitForFontCheck(family, weight, italic, { timeoutMs: 4000 });
    pdfFontLog('FontFace ready', { variant: fontVariantLabel(family, weight, italic) });
    return true;
  } catch (err) {
    fontFaceCache.set(key, false);
    pdfFontLogFail('fontface_embed_error', {
      variant: fontVariantLabel(family, weight, italic),
      binaryEndpoint: fontBinaryApiUrl(family, weight, italic),
      cssEndpoint: fontCssApiUrl(family),
      message: err?.message || String(err),
    });
    if (process.env.NODE_ENV === 'development') {
      console.warn('[fonts] FontFace embed failed', fontVariantLabel(family, weight, italic), err?.message || err);
    }
    return false;
  }
}

/** Load one family + variant for Konva canvas (FontFace + CSS; cached after first load). */
export async function ensureKonvaFontLoaded(family, ty = {}) {
  const name = String(family || '').trim();
  if (!name || isSystemFont(name)) return true;

  const weight = typographyWeight(ty);
  const italic = typographyItalic(ty);

  await ensureGoogleFontsLoaded([name]);
  await ensureFontFaceEmbedded(name, weight, italic);
  if (italic) await ensureFontFaceEmbedded(name, weight, false);
  if (weight === '700') await ensureFontFaceEmbedded(name, '700', italic);

  const specs = [fontLoadSpec(name, weight, italic)];
  await Promise.allSettled(specs.map((spec) => document.fonts.load(spec)));
  await document.fonts.ready;
  return waitForFontCheck(name, weight, italic);
}

/** Wait until every scene font is registered before PDF/html2canvas capture. */
export async function waitForSceneFontsReady(objects, { timeoutMs = 15000 } = {}) {
  const keys = new Set();
  for (const o of objects || []) {
    if (o.type !== 'text') continue;
    const ty = o.typography || {};
    const family = ty.fontFamily;
    if (!family || isSystemFont(family)) continue;
    keys.add(fontFaceKey(family, typographyWeight(ty), typographyItalic(ty)));
  }
  if (!keys.size) return;

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    let ready = true;
    for (const key of keys) {
      const [family, weight, style] = key.split('\0');
      if (!document.fonts.check(fontLoadSpec(family, weight, style === 'italic'))) {
        ready = false;
        break;
      }
    }
    if (ready) return;
    await new Promise((r) => setTimeout(r, 50));
  }

  if (pdfFontLogDepth > 0) {
    for (const key of keys) {
      const [family, weight, style] = key.split('\0');
      const italic = style === 'italic';
      if (!document.fonts.check(fontLoadSpec(family, weight, italic))) {
        pdfFontLogFail('font_check_timeout', {
          variant: fontVariantLabel(family, weight, italic),
          binaryEndpoint: fontBinaryApiUrl(family, weight, italic),
          cssEndpoint: fontCssApiUrl(family),
          timeoutMs,
        });
      }
    }
  }
}

/** Load every font variant used in scene objects before canvas/PDF raster export. */
export async function loadSceneFontVariants(objects) {
  const families = collectFontFamiliesFromObjects(objects);
  await ensureGoogleFontsLoaded(families);

  const specs = new Set(collectFontLoadSpecsFromObjects(objects));
  for (const family of families) {
    specs.add(`400 16px "${family}"`);
    specs.add(`700 16px "${family}"`);
    specs.add(`italic 400 16px "${family}"`);
    specs.add(`italic 700 16px "${family}"`);
  }

  await Promise.allSettled([...specs].map((spec) => document.fonts.load(spec)));
  await document.fonts.ready;
}

function collectFontFaceKeys(objects) {
  const keys = new Set();
  for (const o of objects || []) {
    if (o.type !== 'text') continue;
    const ty = o.typography || {};
    const family = ty.fontFamily;
    if (!family || isSystemFont(family)) continue;
    const weight = typographyWeight(ty);
    const italic = typographyItalic(ty);
    keys.add(fontFaceKey(family, weight, italic));
    keys.add(fontFaceKey(family, weight, false));
    keys.add(fontFaceKey(family, '700', italic));
    keys.add(fontFaceKey(family, '400', false));
  }
  return [...keys];
}

/** Register Google Fonts via FontFace API (required for Konva canvas + PDF export). */
export async function embedFontFacesForScene(objects) {
  await loadSceneFontVariants(objects);

  await Promise.allSettled(
    collectFontFaceKeys(objects).map(async (key) => {
      const [family, weight, style] = key.split('\0');
      await ensureFontFaceEmbedded(family, weight, style === 'italic');
    }),
  );

  await document.fonts.ready;
  await waitForSceneFontsReady(objects);
}

/** Re-apply typography on Konva Text nodes so canvas uses loaded faces. */
export function refreshKonvaTextStyles(stage, sceneObjects) {
  if (!stage) return;

  const typographyById = new Map();
  for (const o of sceneObjects || []) {
    if (o.type === 'text') typographyById.set(o.id, o.typography || {});
  }

  stage.find('Text').forEach((textNode) => {
    try {
      const group = textNode.getParent?.();
      const nodeId = group?.id?.() || group?.name?.();
      const ty = typographyById.get(nodeId) || {};
      const family = ty.fontFamily || textNode.fontFamily() || 'Arial';
      textNode.fontFamily('Arial');
      textNode.fontFamily(family);
      textNode.fontStyle(konvaFontStyleFromTypography(ty));
      textNode.textDecoration('');
      textNode.clearCache();
    } catch {
      /* ignore individual node failures */
    }
  });

  stage.batchDraw();
}

/** Fast editor path ??? CSS + document.fonts only. */
export async function preloadSceneFontsLight(objects) {
  const families = collectFontFamiliesFromObjects(objects);
  if (!families.length) return;
  await ensureGoogleFontsLoaded(families);
  const specs = collectFontLoadSpecsFromObjects(objects);
  if (specs.length) {
    await Promise.allSettled([...specs].map((spec) => document.fonts.load(spec)));
  }
  await document.fonts.ready;
}

/** Apply font when user picks from dropdown ??? embeds FontFace (cached) then refreshes Konva. */
export async function applyFontFamilyQuick(family, ty = {}) {
  return ensureKonvaFontLoaded(family, ty);
}

/** Load fonts for scene objects and refresh Konva canvas if a stage is provided. */
export async function loadSceneFontsAndRefresh(stage, objects) {
  await embedFontFacesForScene(objects);
  if (stage) refreshKonvaTextStyles(stage, objects);
}

let warmSceneGeneration = 0;

/**
 * Download + cache all fonts used in the scene (IndexedDB + FontFace) while editing.
 * Runs in idle time so canvas stays responsive.
 */
export function warmSceneFontsLocally(objects, stage = null) {
  const list = objects || [];
  if (!list.length) return;
  const gen = ++warmSceneGeneration;
  const run = () => {
    if (gen !== warmSceneGeneration) return;
    void embedFontFacesForScene(list).then(() => {
      if (gen !== warmSceneGeneration) return;
      if (stage) refreshKonvaTextStyles(stage, list);
    });
  };
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 2500 });
  } else {
    setTimeout(run, 150);
  }
}

function clearFailedFontFaceCache(objects) {
  for (const key of collectFontFaceKeys(objects || [])) {
    if (fontFaceCache.get(key) === false) fontFaceCache.delete(key);
  }
}

/**
 * Load every scene font from IndexedDB (or network), register FontFace, and verify
 * before canvas/PDF capture ??? same local cache path as the editor.
 */
export async function downloadSceneFontsLocally(objects, stage = null, { forPdf = false } = {}) {
  const list = objects || [];
  if (!list.length) return;

  const run = async () => {
    const families = collectFontFamiliesFromObjects(list);
    const variants = collectFontFaceKeys(list);
    pdfFontLog('start', {
      families,
      variantCount: variants.length,
      useProxy: shouldUseFontProxy(),
    });

    clearFailedFontFaceCache(list);
    await embedFontFacesForScene(list);
    if (stage) refreshKonvaTextStyles(stage, list);
    await waitForSceneFontsReady(list, { timeoutMs: 20000 });
    await document.fonts.ready;
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    pdfFontLog('complete', {
      families,
      variantCount: variants.length,
      failureCount: pdfFontFailures.length,
    });
  };

  if (forPdf) {
    await runWithPdfFontLogging(run);
  } else {
    await run();
  }
}

/** PDF export entry ? ensures locally cached fonts are active on stage before rasterize. */
export async function prepareFontsForPdfExport(stage, objects) {
  await downloadSceneFontsLocally(objects || [], stage, { forPdf: true });
}

/** Wait for fonts + GCP images before capturing a Konva page (export / page switch). */
export async function waitForKonvaSceneReady(stage, scene) {
  const objects = scene?.objects || [];
  if (!objects.length) {
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    return;
  }

  await embedFontFacesForScene(objects);

  await Promise.allSettled(
    objects
      .filter((o) => o.type === 'image')
      .map(async (o) => {
        const key = o.objectKey || o.gcpObjectKey;
        const srcStr = String(o.src || '').trim();
        if (!key && !srcStr) return;
        try {
          let url = '';
          if (srcStr.startsWith('data:') || srcStr.startsWith('blob:')) {
            url = srcStr;
          } else if (key) {
            url = await fetchGcpImageBlobUrl(key);
          } else {
            url = await resolveGcpImageForCanvas(srcStr, key);
          }
          if (!url) return;
          await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = url;
          });
          if (url.startsWith('blob:')) {
            try {
              URL.revokeObjectURL(url);
            } catch {
              /* ignore */
            }
          }
        } catch {
          /* ignore preload failures */
        }
      }),
  );

  if (stage) {
    refreshKonvaTextStyles(stage, objects);
    stage.batchDraw();
  }

  await new Promise((r) => setTimeout(r, 80));
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
}
